import { launch } from "../state";
/** The lock-coding gate refused the request: a record is not complete enough to lock. */
export class LockRefusedError extends Error {
    constructor(problems) {
        super("lock refused");
        this.problems = problems;
    }
}
export class ApiClient {
    constructor(baseUrl = "") {
        this.baseUrl = baseUrl;
    }
    headers() {
        const tok = launch.value?.sessionToken;
        return {
            "Content-Type": "application/json",
            ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        };
    }
    async verifyLaunch(params) {
        const r = await fetch(`${this.baseUrl}/auth/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(params),
        });
        if (!r.ok)
            throw new Error(`verifyLaunch failed: ${r.status}`);
        return r.json();
    }
    async getCase(scenarioId) {
        const r = await fetch(`${this.baseUrl}/case/${scenarioId}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`getCase failed: ${r.status}`);
        return r.json();
    }
    async getPasActivity(id) {
        const r = await fetch(`${this.baseUrl}/pas/activity/${id}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`getPasActivity failed: ${r.status}`);
        return r.json();
    }
    async getBatch(batchId) {
        const r = await fetch(`${this.baseUrl}/batch/${batchId}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`getBatch failed: ${r.status}`);
        return r.json();
    }
    async saveDraft(scenarioId, draft) {
        const r = await fetch(`${this.baseUrl}/case/${scenarioId}/save`, {
            method: "POST",
            headers: this.headers(),
            body: JSON.stringify(draft),
        });
        if (!r.ok)
            throw new Error(`saveDraft failed: ${r.status}`);
        return r.json();
    }
    async loadDraft(scenarioId) {
        const r = await fetch(`${this.baseUrl}/case/${scenarioId}/draft`, { headers: this.headers() });
        if (r.status === 204)
            return null;
        if (!r.ok)
            throw new Error(`loadDraft failed: ${r.status}`);
        return r.json();
    }
    async submitCase(scenarioId, draft) {
        const r = await fetch(`${this.baseUrl}/case/${scenarioId}/submit`, {
            method: "POST", headers: this.headers(), body: JSON.stringify(draft),
        });
        if (!r.ok)
            throw new Error(`submitCase failed: ${r.status}`);
        return r.json();
    }
    async submitPasActivity(id, draft) {
        const r = await fetch(`${this.baseUrl}/pas/activity/${id}/submit`, {
            method: "POST", headers: this.headers(), body: JSON.stringify(draft),
        });
        if (!r.ok)
            throw new Error(`submitPasActivity failed: ${r.status}`);
        return r.json();
    }
    async submitBatch(batchId, items) {
        const r = await fetch(`${this.baseUrl}/batch/${batchId}/submit`, {
            method: "POST", headers: this.headers(), body: JSON.stringify({ items }),
        });
        if (!r.ok)
            throw new Error(`submitBatch failed: ${r.status}`);
        return r.json();
    }
    // ----- own-work coding audit (batch kind "own-work-audit") -----
    async lockCoding(batchId, drafts) {
        const r = await fetch(`${this.baseUrl}/batch/${batchId}/lock-coding`, {
            method: "POST", headers: this.headers(), body: JSON.stringify({ drafts }),
        });
        if (r.status === 400) {
            const body = await r.json().catch(() => ({}));
            throw new LockRefusedError(body?.detail?.problems ?? {});
        }
        if (!r.ok)
            throw new Error(`lockCoding failed: ${r.status}`);
        return r.json();
    }
    async submitOwnWorkAudit(batchId, payload) {
        const r = await fetch(`${this.baseUrl}/batch/${batchId}/audit`, {
            method: "POST", headers: this.headers(), body: JSON.stringify(payload),
        });
        if (!r.ok)
            throw new Error(`submitOwnWorkAudit failed: ${r.status}`);
        return r.json();
    }
    async submitOwnWorkReview(batchId, payload) {
        const r = await fetch(`${this.baseUrl}/batch/${batchId}/review`, {
            method: "POST", headers: this.headers(), body: JSON.stringify(payload),
        });
        if (!r.ok)
            throw new Error(`submitOwnWorkReview failed: ${r.status}`);
        return r.json();
    }
    async requestUnlock(params) {
        const r = await fetch(`${this.baseUrl}/assessment/unlock-request`, {
            method: "POST", headers: this.headers(), body: JSON.stringify(params),
        });
        if (!r.ok)
            throw new Error(`requestUnlock failed: ${r.status}`);
        return r.json();
    }
    async autocomplete(q, edition, classification) {
        const params = new URLSearchParams({ q, edition });
        if (classification)
            params.set("classification", classification);
        const r = await fetch(`${this.baseUrl}/api/codes/ac?${params.toString()}`, { headers: this.headers() });
        if (!r.ok)
            throw new Error(`autocomplete failed: ${r.status}`);
        return r.json();
    }
    async tosAccept(version = "v1") {
        await fetch(`${this.baseUrl}/tos/accept`, {
            method: "POST", headers: this.headers(), body: JSON.stringify({ version }),
        });
    }
    async tosStatus() {
        const r = await fetch(`${this.baseUrl}/tos/status`, { headers: this.headers() });
        return r.json();
    }
}
export const api = new ApiClient("");
